import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { ChevronRight } from "lucide-react";

const STATUS_CLS = {
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  "sent (mock)": "bg-sky-100 text-sky-700 border-sky-200",
  failed: "bg-rose-100 text-rose-700 border-rose-200",
  queued: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminEmailLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const reload = async (templateKey) => {
    setLoading(true);
    try {
      const params = templateKey ? { template_key: templateKey, limit: 100 } : { limit: 100 };
      const { data } = await api.get("/admin/email-log", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(""); }, []);

  if (user && user.system_role !== "platform_admin") {
    return <AppShell><div className="p-8 text-rose-600">Platform admin only.</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-4" data-testid="admin-email-log-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Email log</h1>
            <p className="text-sm text-slate-600">{total} total · Resend integration</p>
          </div>
          <div className="flex gap-2">
            <Input data-testid="email-log-filter-input" placeholder="Filter by template_key…" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-64" />
            <Button onClick={() => reload(filter)} variant="outline">Filter</Button>
            <Button onClick={() => { setFilter(""); reload(""); }} variant="ghost">Clear</Button>
          </div>
        </div>
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-slate-500 text-sm">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No emails sent yet. Trigger a chair action (invite, levy, quote request, etc.) and they'll appear here.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2">When</th>
                    <th className="px-4 py-2">Template</th>
                    <th className="px-4 py-2">To</th>
                    <th className="px-4 py-2">Subject</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="email-log-rows">
                  {rows.map((r) => (
                    <tr
                      key={r.email_id}
                      className="border-b border-slate-100 cursor-pointer hover:bg-sky-50/40 transition-colors"
                      onClick={() => setSelected(r)}
                      data-testid={`email-log-row-${r.email_id}`}
                    >
                      <td className="px-4 py-2 text-slate-600 font-mono text-xs">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-2"><Badge variant="outline" className="font-mono text-xs">{r.template_key}</Badge></td>
                      <td className="px-4 py-2 text-slate-700">{r.to_email}</td>
                      <td className="px-4 py-2 text-slate-700 truncate max-w-xs">{r.subject}</td>
                      <td className="px-4 py-2"><Badge className={STATUS_CLS[r.status] || STATUS_CLS.queued}>{r.status}</Badge></td>
                      <td className="px-2 py-2 text-slate-400"><ChevronRight size={14} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="email-detail-drawer">
          {selected && (
            <>
              <SheetHeader className="border-b border-slate-100 pb-4">
                <SheetTitle className="text-lg font-semibold text-slate-900 break-words pr-6">{selected.subject || "(no subject)"}</SheetTitle>
                <SheetDescription className="flex items-center gap-2 flex-wrap mt-2">
                  <Badge variant="outline" className="font-mono text-xs" data-testid="email-detail-template">{selected.template_key}</Badge>
                  <Badge className={STATUS_CLS[selected.status] || STATUS_CLS.queued} data-testid="email-detail-status">{selected.status}</Badge>
                  <span className="text-xs text-slate-500 font-mono">{selected.created_at?.slice(0, 19).replace("T", " ")}</span>
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-4 pt-4 text-sm">
                <Section label="Recipients">
                  <Field name="To" value={selected.to_email} testid="email-detail-to" />
                  {selected.cc && selected.cc.length > 0 && (
                    <Field name="Cc" value={selected.cc.join(", ")} testid="email-detail-cc" />
                  )}
                  <Field name="From" value={selected.payload?.variables ? (selected.payload?.from || "Opstrait <noreply@opstrai.com>") : "(stored in payload)"} testid="email-detail-from" />
                </Section>

                <Section label="Metadata">
                  {selected.scheme_id && <Field name="Scheme" value={selected.scheme_id} testid="email-detail-scheme" mono />}
                  <Field name="Sent at" value={selected.sent_at || "—"} mono />
                  <Field name="Resend message id" value={selected.resend_message_id || "(none — mock mode)"} mono />
                  {selected.error_message && <Field name="Error" value={selected.error_message} mono className="text-rose-700" />}
                </Section>

                {selected.payload?.variables && (
                  <Section label="Template variables">
                    <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-words text-slate-700" data-testid="email-detail-variables">
                      {JSON.stringify(selected.payload.variables, null, 2)}
                    </pre>
                  </Section>
                )}

                {selected.payload?.html_excerpt && (
                  <Section label="Rendered email preview">
                    <div className="rounded-md border border-slate-200 overflow-hidden">
                      <iframe
                        title="rendered email"
                        srcDoc={selected.payload.html_excerpt}
                        sandbox=""
                        className="w-full"
                        style={{ minHeight: "520px", border: "0", display: "block" }}
                        data-testid="email-detail-iframe"
                      />
                    </div>
                    {selected.template_key === "broker_quote_intake" && selected.payload.html_excerpt.includes("Submitted via Opstrait") && (
                      <p className="mt-2 text-xs text-emerald-700 font-medium" data-testid="email-detail-attribution-confirmed">
                        ✓ "Submitted via Opstrait" attribution footer is present in this email.
                      </p>
                    )}
                  </Section>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ name, value, testid, mono, className }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="w-32 shrink-0 text-slate-500">{name}</div>
      <div className={`flex-1 break-words ${mono ? "font-mono text-xs" : ""} ${className || "text-slate-800"}`} data-testid={testid}>{value || "—"}</div>
    </div>
  );
}
